// ADR 5.9.26u §2 — one measurement, three decisions.
//
// `document.documentElement.clientWidth`, not `window.innerWidth`. The
// difference is a classic desktop scrollbar, which `innerWidth` counts and the
// layout viewport does not: a 590px layout in a browser with a 15px scrollbar
// reads as 605 through `innerWidth` and lands in Medium, showing a rail to a
// window Material calls Compact. `clientWidth` is the number CSS lays the page
// out against, which is the number the table is about.
//
// ADR 17.9.26f §2 — the measurement has a second input: the root font size.
// The table is in dp, and on the web dp was CSS px (5.9.26u) *at 100%*. With
// `index.css`'s desktop scale on, one dp is 1.2 CSS px, and the shell must
// see the width the content sees — which is what browser zoom did for free,
// shrinking the CSS-px viewport as it grew every `rem`. So `clientWidth` is
// divided by the root scale before the table is asked. The stylesheet is
// applied before the first render in both builds (dev imports it before
// `render`; the production `<link>` blocks the module script), so the
// mount-time reading is current, and §1's flip happens on a resize the
// listener already sees.
//
// Not `matchMedia`, either. The boundaries arrive from JSON at runtime, so a
// media query would have to be built from a string containing the numbers —
// a second copy of exactly the values 5.9.26b exists to keep in one place.
//
// The `resize` listener is debounced by one animation frame: a drag fires the
// event per pixel, and the answer can only change at three of those pixels.
// `window.requestAnimationFrame` is named on `window` rather than called bare
// so a test can flush the frame deterministically.

import { useEffect, useRef, useState } from 'react'

import { TEXT_SCALE_EVENT } from '../display/textScale'
import type { NavChrome, PaneLayout, WindowSizeClass } from '../wasm'
// ADR 25.9.26f §2 — the TypeScript twin, not the facade: this hook runs in the
// first frame, which must paint before the wasm has arrived.
import { navChrome, paneLayout, windowSizeClass } from './windowSizeTable'
import { readSpread, type SpreadFn, type SpreadRequest, type SpreadValue } from './spread/readSpread'
import { NO_FOLD_SIGNALS, readFoldSignals, readRtl, watchFoldSignals } from './spread/signals'
import { loadSpreadFacade } from './spread/spreadFacade'
import { AUTO, spreadPreferenceStore } from './spread/spreadPref'

// ADR 9.9.26t §5 — re-exported so consumers name the three unions without each
// reaching into the facade. `breakpoints.ts` was that door while the table was
// vendored; the table is not vendored any more, so the door moved rather than
// closed. The union members are unchanged: `serde(rename_all = "snake_case")`
// on the Rust enums emits the same `'compact' | 'medium' | 'expanded'` the
// fixture's `expect` column has always spelled.
export type { NavChrome, PaneLayout, WindowSizeClass }

/** The three answers, from one reading of the layout viewport. */
export interface WindowClass {
  sizeClass: WindowSizeClass
  paneLayout: PaneLayout
  navChrome: NavChrome
}

// The browser's initial font size (16px), not a table value: the scale is the
// root's computed size over the size every `rem` in the tree was drawn at.
const ROOT_PX = 16

/**
 * ADR 17.9.26f §2 — how many CSS px one dp is right now.
 *
 * Any reading that is not a finite positive number is scale 1 — `''` from a
 * jsdom with no stylesheet, or a keyword — so every layout test that stubs
 * `clientWidth` keeps measuring in CSS px, and a test opts in by setting an
 * inline `fontSize` on the root.
 *
 * Exported for 7.10.26z §1: the Tabletop panel places the dp fold in CSS px.
 */
export function rootScale(): number {
  const size = parseFloat(getComputedStyle(document.documentElement).fontSize)
  return Number.isFinite(size) && size > 0 ? size / ROOT_PX : 1
}

interface Dims {
  widthDp: number
  heightDp: number
  scale: number
}

function dims(): Dims {
  const root = document.documentElement
  const scale = rootScale()
  // Rounded to the nearest dp: the classifier takes whole dp, as the wasm's
  // `u32` signatures did, and a quotient
  // that falls a hair under a boundary must not demote the class at the exact
  // width §1 derives from it (720 / 1.2 is 600 by the stated rule, not by
  // which side of the integer an IEEE quotient lands).
  return {
    widthDp: Math.round(root.clientWidth / scale),
    heightDp: Math.round(root.clientHeight / scale),
    scale,
  }
}

/** The three answers for a window of this size — the whole window's, or one page's (7.10.26v §2). */
function classify(widthDp: number, heightDp: number): WindowClass {
  return {
    sizeClass: windowSizeClass(widthDp),
    paneLayout: paneLayout(widthDp, heightDp),
    navChrome: navChrome(widthDp),
  }
}

function same(a: WindowClass, b: WindowClass): boolean {
  return (
    a.sizeClass === b.sizeClass && a.paneLayout === b.paneLayout && a.navChrome === b.navChrome
  )
}

/** ADR 7.10.26v §2 — the window's class and the spread, from one measurement. */
export interface WindowReading {
  windowClass: WindowClass
  spread: SpreadValue
}

/** Every field of the spread; the two page classes compare by identity (`keep` made them stable). */
const SPREAD_FIELDS: readonly (keyof SpreadValue)[] = [
  'layout', 'leading', 'companion', 'splitDp', 'gapDp', 'companionPx', 'gapPx', 'canSplit', 'preference', 'answered',
]

function sameSpread(a: SpreadValue, b: SpreadValue): boolean {
  return SPREAD_FIELDS.every((field) => a[field] === b[field])
}

/** The first of `candidates` that answers as `fresh` does, so a page's class keeps its identity. */
function keep(fresh: WindowClass, ...candidates: (WindowClass | undefined)[]): WindowClass {
  return candidates.find((candidate) => candidate !== undefined && same(candidate, fresh)) ?? fresh
}

/**
 * One re-read. Each half is replaced only when one of its answers moved, so the
 * analytics effect (keyed on `windowClass`) and the shell (keyed on `spread`)
 * re-render exactly at their own crossings.
 */
function nextReading(previous: WindowReading | null, ask: SpreadFn | null, withSpread: boolean): WindowReading {
  const measured = dims()
  const windowClass = keep(classify(measured.widthDp, measured.heightDp), previous?.windowClass)
  const raw = readSpread(withSpread ? spreadRequest(measured, ask) : flatRequest(measured), classify)
  const spread = stableSpread(raw, windowClass, previous?.spread)
  if (previous !== null && previous.windowClass === windowClass && previous.spread === spread) return previous
  return { windowClass, spread }
}

/** The facade's question for this measurement: the fold, the stored preference, the direction. */
function spreadRequest({ widthDp, heightDp, scale }: Dims, ask: SpreadFn | null): SpreadRequest {
  return {
    ask,
    measured: { widthDp, heightDp, ...readFoldSignals(scale) },
    preference: spreadPreferenceStore.read(),
    rtl: readRtl(),
    scale,
  }
}

/** `useWindowClass()`'s: no facade, no fold, no preference read — always one page. */
function flatRequest({ widthDp, heightDp, scale }: Dims): SpreadRequest {
  return { ask: null, measured: { widthDp, heightDp, ...NO_FOLD_SIGNALS }, preference: AUTO, rtl: false, scale }
}

/** The page classes keep their identity, and so does the spread when nothing in it moved. */
function stableSpread(raw: SpreadValue, windowClass: WindowClass, before: SpreadValue | undefined): SpreadValue {
  const composed = {
    ...raw,
    leading: keep(raw.leading, windowClass, before?.leading),
    companion: keep(raw.companion, windowClass, before?.companion),
  }
  return before !== undefined && sameSpread(before, composed) ? before : composed
}

/** Every signal that can move the reading; returns the unsubscribe. */
function listen(onChange: () => void, withSpread: boolean): () => void {
  window.addEventListener('resize', onChange)
  // ADR 30.9.26i §4 — a text-size change moves the root scale with no resize.
  window.addEventListener(TEXT_SCALE_EVENT, onChange)
  const extra = withSpread ? [watchFoldSignals(onChange), spreadPreferenceStore.subscribe(onChange)] : []
  return () => {
    window.removeEventListener('resize', onChange)
    window.removeEventListener(TEXT_SCALE_EVENT, onChange)
    extra.forEach((stop) => stop())
  }
}

/**
 * The shared body of both hooks. `spread: false` attaches only the two
 * listeners 5.9.26u's hook always had, never loads the facade and never
 * subscribes to the preference, so `useWindowClass()` is unchanged.
 */
function useMeasured({ spread }: { spread: boolean }): WindowReading {
  const ask = useRef<SpreadFn | null>(null)
  const [value, setValue] = useState<WindowReading>(() => nextReading(null, null, spread))

  useEffect(() => {
    let frame = 0
    let live = true
    const onChange = () => {
      if (frame !== 0) return
      frame = window.requestAnimationFrame(() => {
        frame = 0
        setValue((previous) => nextReading(previous, ask.current, spread))
      })
    }
    // A resize may already have happened between the first render and this
    // effect; re-reading here costs one measurement and closes that window.
    onChange()
    const stop = listen(onChange, spread)
    // ADR 7.10.26v §3 — after the first paint; `OnePage` until then.
    if (spread) {
      void loadSpreadFacade().then((loaded) => {
        if (!live || loaded === null) return
        ask.current = loaded
        onChange()
      })
    }
    return () => {
      live = false
      if (frame !== 0) window.cancelAnimationFrame(frame)
      stop()
    }
  }, [spread])

  return value
}

/**
 * The current window class, updated on resize.
 *
 * Returns the *previous* object when a resize did not cross a boundary, so a
 * drag across 300 pixels re-renders the shell at most once per crossing rather
 * than three hundred times.
 */
export function useWindowClass(): WindowClass {
  return useMeasured({ spread: false }).windowClass
}

/** ADR 7.10.26v §2 — the class and the spread together: the provider's one call. */
export function useWindowReading(): WindowReading {
  return useMeasured({ spread: true })
}
